import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

const SmallMap = ({ mapobj }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const createMap = () => {
    // Ensure ESRI/Dojo API is loaded globally
    if (!window.esri || !window.dojo || !window.require) {
      console.error(
        "ESRI or Dojo API not loaded before SmallMap component mount."
      );
      return;
    }
    if (!mapRef.current) {
      console.error("Map container ref not available.");
      return;
    }
    // Prevent re-initialization
    if (mapInstanceRef.current) {
      return;
    }

    const { lat, lng, area } = mapobj;
    const mapDiv = mapRef.current;
    const fillRGB = [127, 190, 209, 0.5];
    const white = "#ffffff";
    const zoom = 6;

    // Use AMD require to load ESRI modules (like the original CoffeeScript)
    window.require(
      [
        "esri/map",
        "esri/geometry/Point",
        "esri/geometry/Circle",
        "esri/geometry/Polygon",
        "esri/symbols/SimpleMarkerSymbol",
        "esri/symbols/SimpleLineSymbol",
        "esri/symbols/SimpleFillSymbol",
        "esri/graphic",
        "dojo/domReady!",
      ],
      (
        Map,
        Point,
        Circle,
        Polygon,
        SimpleMarkerSymbol,
        SimpleLineSymbol,
        SimpleFillSymbol,
        Graphic
      ) => {
        console.log("ESRI modules loaded successfully");

        const drawCircleSymbol = (fillColor, lineColor) => {
          const circleSymbol = new SimpleFillSymbol();
          const dojoFill = new window.dojo.Color(fillColor);
          const dojoLine = new window.dojo.Color(lineColor);
          const outline = new SimpleLineSymbol(
            SimpleLineSymbol.STYLE_SOLID,
            dojoLine,
            3.0
          );
          circleSymbol.setOutline(outline);
          circleSymbol.setColor(fillColor); // Use fillColor directly like original
          return circleSymbol;
        };

        const mapLoaded = (mapInstance) => {
          const symbol = drawCircleSymbol(fillRGB, white);
          let graphic;

          if (area && Array.isArray(area) && area.length > 0) {
            let combinedExtent = null;
            area.forEach((a, index) => {
              try {
                const areaArray = JSON.parse(a); // Parse JSON string
                const polygon = new Polygon(areaArray);
                graphic = new Graphic(polygon, symbol, mapobj); // Pass attributes if needed
                mapInstance.graphics.add(graphic);

                if (index === 0) {
                  // Use first polygon extent like original code
                  const extent = graphic.geometry.getExtent();
                  const expandedExtent = extent.expand(7.0); // Use same expand factor as original
                  mapInstance.setExtent(expandedExtent);
                }
              } catch (e) {
                console.error(
                  "Error parsing or creating polygon from area data:",
                  a,
                  e
                );
              }
            });
          } else if (lat != null && lng != null) {
            // Fallback to drawing a circle if no valid area provided
            const centerPoint = new Point([lng, lat]);
            const circleGeometry = new Circle({
              center: centerPoint,
              geodesic: true,
              radius: 60000, // meters
            });
            graphic = new Graphic(circleGeometry, symbol, mapobj);
            mapInstance.graphics.add(graphic);
          } else {
            console.warn(
              "SmallMap: No valid area or lat/lng provided in mapobj.",
              mapobj
            );
          }
        };

        // --- Map Initialization ---
        const map = new Map(mapDiv.id, {
          center: [lng || -98.57, lat || 39.82], // Use provided or default US center
          zoom: zoom,
          basemap: "national-geographic",
          slider: true, // Show zoom slider
        });
        mapInstanceRef.current = map; // Store map instance

        map.on("load", () => mapLoaded(map));

        // Optional: Add error handling for map creation
        map.on("error", (err) => {
          console.error("SmallMap ESRI Map Error:", err);
        });
      }
    ); // Close AMD require callback
  };

  useEffect(() => {
    createMap();

    return () => {
      if (mapInstanceRef.current) {
        console.log("Destroying SmallMap instance");
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div>
      {/* Ensure the map container has a defined size via CSS or style */}
      <div
        id="tribe_map_canvas"
        ref={mapRef}
        style={{ width: "100%", height: "250px" }} // Example size, adjust as needed
      ></div>
    </div>
  );
};

SmallMap.propTypes = {
  mapobj: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    area: PropTypes.arrayOf(PropTypes.string), // Expecting array of JSON strings
  }).isRequired,
};

export default SmallMap;
